import { useEffect } from "react";
import Link from "next/link";
import {
  FacebookShareButton,
  EmailShareButton,
  WhatsappShareButton,
} from "next-share";
import type { Compilation } from "@memento/types/compilation";
import { Feature } from "@memento/library/feature-access";
import { GuardCanUseFeature } from "src/components/GuardCanUseFeature";
import { SimpleLink } from "src/components/SimpleLink";
import { VideoPlayer } from "src/components/VideoPlayer/VideoPlayer";
import { DashboardSection } from "src/components/Dashboard/DashboardSection";
import { Alert } from "@memento/ui/Alert";
import { useSubscription } from "src/hooks/useSubscription";
import { useAds } from "src/hooks/useAds";
import { getWatchCompilationUrl } from "src/libs/compilation-lib";
import constants from "src/constants";
import { VideoSource } from "src/libs/segment-lib";
import { useCanUseFeature } from "src/hooks/useCanUseFeature";

type PlaceholderProps = {
  compilation: Compilation;
  latestCompilation: Compilation | null;
  isRecipientView: boolean;
};

function Placeholder({
  compilation,
  latestCompilation,
  isRecipientView,
}: PlaceholderProps) {
  const titleText = compilation?.card ? compilation.card.title.trim() : "";

  return (
    <DashboardSection title={titleText}>
      {latestCompilation ? (
        <Alert variant="notice">
          This Group Video has been archived. Check out the latest version{" "}
          <SimpleLink
            path={getWatchCompilationUrl({
              compilationId: latestCompilation.compilationId,
              absolutePath: false,
              forRecipient: isRecipientView,
            })}
            queryParams={
              isRecipientView
                ? { [constants.reactionRecipientViewUrlParam]: "1" }
                : {}
            }
          >
            here
          </SimpleLink>
          !
        </Alert>
      ) : (
        <Alert variant="notice">
          This Group Video has been archived. Please contact the organizer for
          the latest version.
        </Alert>
      )}
    </DashboardSection>
  );
}

type ViewGroupVideoProps = {
  compilation: Compilation;
  latestCompilation: Compilation | null;
  isViewable: boolean;
  isRecipientView: boolean;
  fullUrl: string;
};

export function ViewGroupVideo({
  compilation,
  latestCompilation,
  isViewable,
  isRecipientView,
  fullUrl,
}: ViewGroupVideoProps) {
  const { subscription } = useSubscription();
  const { updateAds } = useAds();

  const card = compilation?.card;

  const canDownload = useCanUseFeature(Feature.MontageDownload, {
    card,
  });

  useEffect(() => {
    updateAds(card, subscription);
  }, [card, subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isViewable) {
    return (
      <div className="mx-auto mt-8 w-full lg:w-2/3">
        <Placeholder
          compilation={compilation}
          latestCompilation={latestCompilation}
          isRecipientView={isRecipientView}
        />
      </div>
    );
  }

  if (!compilation?.card) {
    return null;
  }

  const titleText = compilation.card.title.trim();

  const shareText = 'Check out this Memento video, "' + titleText + '"!';

  const compSources: VideoSource[] = [
    { src: compilation.videoUrl, type: "video/mp4" },
  ];

  if (compilation.videoCdnUrl !== undefined && compilation.videoCdnUrl) {
    compSources.unshift({
      src: compilation.videoCdnUrl,
      type: "video/mp4",
    });
  }

  return (
    // Due to the way browser rendering engines handle layout, combining
    // 2/3 (66.6666667%) and 16 / 9 (56.25%) used for the video can cause a gap on the sides
    // of the video player. Using a whole-number percent gives us about the same size
    // without the gap.
    <div className="mx-auto w-full rounded-2xl bg-white/80 backdrop-blur-sm lg:w-[66%]">
      <div className="mt-8 overflow-hidden rounded-t-2xl">
        <VideoPlayer
          sources={compSources}
          thumbUrl={compilation.thumbUrl}
          controls={
            canDownload
              ? ["mute", "fullscreen", "download"]
              : ["mute", "fullscreen"]
          }
        />
      </div>
      <div className="flex flex-col justify-between gap-3 px-6 py-7 md:flex-row md:items-center">
        <h1 className="text-xl text-gray-700">{titleText}</h1>
        <div className="flex items-center gap-1 md:gap-2">
          <div className="mr-2 text-base text-gray-700">Share</div>
          <FacebookShareButton quote={shareText} url={fullUrl}>
            <img
              src="/images/create-group-video/share-facebook.svg"
              alt="Facebook icon"
              className="h-6 w-6 sm:h-10 sm:w-10"
            />
          </FacebookShareButton>
          <WhatsappShareButton title={shareText} url={fullUrl}>
            <img
              src="/images/create-group-video/share-whatsapp.svg"
              alt="WhatsApp logo"
              className="h-6 w-6 sm:h-10 sm:w-10"
            />
          </WhatsappShareButton>
          <EmailShareButton subject={shareText} url={fullUrl}>
            <img
              src="/images/create-group-video/share-email.svg"
              alt="Web link icon"
              className="h-6 w-6 sm:h-10 sm:w-10"
            />
          </EmailShareButton>
          <GuardCanUseFeature
            feature={Feature.MontageDownload}
            card={compilation.card}
          >
            <Link href={compilation.videoCdnUrl}>
              <img
                src="/images/create-group-video/share-download.svg"
                alt="Download icon"
                className="h-6 w-6 sm:h-10 sm:w-10"
              />
            </Link>
          </GuardCanUseFeature>
        </div>
      </div>
    </div>
  );
}
